<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="flex max-h-[85vh] flex-col gap-6 p-6 sm:max-w-[600px]">
            <DialogHeader class="space-y-2">
                <DialogTitle class="text-xl font-medium text-new-neutral-800">
                    Copy installation code
                </DialogTitle>
                <DialogDescription class="text-xs text-muted-foreground">
                    For more details
                    <a
                        href="https://docs.topol.io/plugin"
                        target="_blank"
                        class="text-new-blue-500 hover:underline"
                        >check out our documentation</a
                    >.
                </DialogDescription>
            </DialogHeader>

            <!-- Framework tabs -->
            <nav
                class="flex flex-wrap items-center gap-1 border-b border-border"
                aria-label="Framework"
            >
                <button
                    v-for="fw in frameworkList"
                    :key="fw.key"
                    type="button"
                    class="flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm whitespace-nowrap transition-colors"
                    :class="
                        activeFramework === fw.key
                            ? 'border-new-topol-blue bg-surface-card text-foreground dark:bg-muted'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    "
                    @click="activeFramework = fw.key"
                >
                    {{ fw.label }}
                </button>
            </nav>

            <!-- Snippets -->
            <div class="flex min-h-[420px] flex-1 flex-col gap-3 overflow-y-auto pr-1">
                <template v-if="highlightedSteps.length">
                    <div
                        v-for="(step, i) in highlightedSteps"
                        :key="`${activeFramework}-${i}`"
                        class="flex flex-col gap-3"
                    >
                        <p class="text-xs text-muted-foreground">{{ step.text }}</p>

                        <!-- Single-line snippet (e.g. install command) -->
                        <div
                            v-if="step.inline"
                            class="flex items-center border border-[#484e59] bg-[#303540]"
                        >
                            <div
                                class="min-w-0 flex-1 overflow-x-auto px-3 py-1 text-sm [&>pre]:!m-0 [&>pre]:!bg-transparent [&>pre]:whitespace-pre"
                                v-html="step.html"
                            ></div>
                            <button
                                type="button"
                                class="shrink-0 p-2 text-new-neutral-300 transition-colors hover:text-white"
                                :aria-label="copied[i] ? 'Copied' : 'Copy'"
                                @click="copy(step.raw, i)"
                            >
                                <component :is="copied[i] ? Check : Copy" class="size-4" />
                            </button>
                        </div>

                        <!-- Multi-line snippet -->
                        <div v-else class="relative border border-[#484e59] bg-[#303540]">
                            <button
                                type="button"
                                class="absolute right-1 top-1 z-10 p-2 text-new-neutral-300 transition-colors hover:text-white"
                                :aria-label="copied[i] ? 'Copied' : 'Copy'"
                                @click="copy(step.raw, i)"
                            >
                                <component :is="copied[i] ? Check : Copy" class="size-4" />
                            </button>
                            <div
                                class="overflow-x-auto p-4 text-sm [&>pre]:!m-0 [&>pre]:!bg-transparent [&>pre]:whitespace-pre"
                                v-html="step.html"
                            ></div>
                        </div>
                    </div>
                </template>

                <!-- Loading skeleton -->
                <template v-else>
                    <div class="h-3 w-1/3 animate-pulse bg-new-neutral-200"></div>
                    <div class="space-y-2 bg-[#303540] p-4">
                        <div
                            v-for="w in ['58%', '34%', '46%', '20%', '40%']"
                            :key="w"
                            class="h-3 animate-pulse bg-white/10"
                            :style="{ width: w }"
                        ></div>
                    </div>
                </template>
            </div>

            <DialogFooter class="shrink-0">
                <Button variant="ghost" size="lg" @click="handleClose">Close</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, computed, PropType } from 'vue';
import { useClipboard } from '@vueuse/core';
import { codeToHtml } from 'shiki';
import { Copy, Check } from 'lucide-vue-next';
import IToken from '@/types/Plugin/IToken';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

const emit = defineEmits(['close', 'update:open']);

type FrameworkKey = 'html' | 'package';

interface Step {
    text: string;
    code: string;
    lang: string;
    inline: boolean;
}

const frameworkList: Array<{ key: FrameworkKey; label: string }> = [
    { key: 'html', label: 'HTML' },
    { key: 'package', label: 'JS/TS Package' },
];

const activeFramework = ref<FrameworkKey>('html');

const apiKey = computed(() => props.apiToken.token ?? 'YOUR_API_TOKEN');

const htmlDivCode = `<div id="app" style="position: absolute; width: 100%; height: 100%"></div>`;

// `<\/script>` escapes the closing tag so the surrounding Vue SFC <script> block
// isn't terminated when the file is parsed.
const htmlScriptCode = computed(
    () => `<!--Import Editor Loader-->
${'<script src="https://v3.email-assets.topol.io/loader/build.js" type="text/javascript"></' + 'script>'}

<!--Define Plugin Options-->
<script>
const TOPOL_OPTIONS = {
  id: "#app",
  authorize: {
    apiKey: "${apiKey.value}",
    userId: "User-ID",
  },
  callbacks: {
    onSave: function(json, html) {
      // Handle save
    },
  },
};
${'</' + 'script>'}`,
);

const packageScriptCode = computed(
    () => `import TopolPlugin from "@topol.io/editor";

const TOPOL_OPTIONS = {
  id: "#app",
  authorize: {
    apiKey: "${apiKey.value}",
    userId: "some-user-id",
  },
  callbacks: {
    onSave(json, html) {},
  },
};

TopolPlugin.init(TOPOL_OPTIONS);`,
);

const frameworkSteps = computed<Record<FrameworkKey, Step[]>>(() => ({
    html: [
        {
            text: 'First, insert this HTML within your application:',
            code: htmlDivCode,
            lang: 'html',
            inline: true,
        },
        {
            text: 'Then add the loader and define plugin options:',
            code: htmlScriptCode.value,
            lang: 'html',
            inline: false,
        },
    ],
    package: [
        {
            text: 'Install the editor from npm or yarn:',
            code: 'npm install @topol.io/editor',
            lang: 'bash',
            inline: true,
        },
        {
            text: 'Import TopolPlugin and initialize the editor:',
            code: packageScriptCode.value,
            lang: 'typescript',
            inline: false,
        },
    ],
}));

const highlightedSteps = ref<
    Array<{ text: string; html: string; raw: string; inline: boolean }>
>([]);

const buildSteps = async () => {
    const steps = frameworkSteps.value[activeFramework.value];
    const results = await Promise.all(
        steps.map(async (step) => ({
            text: step.text,
            raw: step.code,
            inline: step.inline,
            html: await codeToHtml(step.code, { lang: step.lang, theme: 'dracula' }),
        })),
    );
    highlightedSteps.value = results;
    Object.keys(copied).forEach((key) => (copied[Number(key)] = false));
};

watch(
    [activeFramework, () => props.open],
    () => {
        if (props.open) {
            highlightedSteps.value = [];
            buildSteps();
        }
    },
    { immediate: true },
);

const { copy: copyToClipboard } = useClipboard();
const copied = reactive<Record<number, boolean>>({});

const copy = async (text: string, index: number) => {
    await copyToClipboard(text);
    copied[index] = true;
    setTimeout(() => {
        copied[index] = false;
    }, 2000);
};

const handleClose = () => {
    emit('close');
    emit('update:open', false);
};
</script>
